import { datadogLogs } from '@datadog/browser-logs';
import {
  datadogRum,
  type RumBeforeSend,
  type RumInitConfiguration,
} from '@datadog/browser-rum-slim';

import { stripClerkTokens } from './pageview-url';
import { redactEmails } from './redact-emails';
import {
  BUILD_ID,
  isStaleChunkError,
  reloadPending,
} from './stale-chunk-reload';

// Error reporting for the app: Datadog RUM (errors) and Logs (info).

// Matched after any non-word character, not just ?&#: a console message can
// carry the bare `slack_link=...` mid-sentence.
const CALLBACK_TOKEN =
  /(^|\W)((?:teams_connect|teams_link|slack_link)=)[^&#\s"'<>]*/gi;
// Clerk tickets and session tokens (`__clerk_ticket` carries the staff
// impersonation token from Org Hub), raw or URL-encoded inside redirect_url.
// ClerkTicketHandler only clears the URL after React mounts, so the first
// page view still has it.
const CLERK_TOKEN = /(__clerk_\w+(?:=|%3D))(?:(?!%26|%23)[^&#\s"'<>])*/gi;

/** Slack/Teams link tokens and Clerk tokens out of a telemetry string. */
export const redactCallbackText = (text: string): string =>
  (/^https?:\/\//i.test(text) ? stripClerkTokens(text) : text)
    .replace(CALLBACK_TOKEN, '$1$2[redacted]')
    .replace(CLERK_TOKEN, '$1[redacted]');

const redactText = (text: string) => redactEmails(redactCallbackText(text));

// Datadog hands beforeSend its own copy of the event and keeps only edits to
// its editable fields, so scrubbing every string in place is safe. Opaque
// objects (DOM nodes, Dates...) are left alone.
function scrub(value: unknown, seen = new WeakSet<object>()): void {
  if (!value || typeof value !== 'object' || seen.has(value)) return;
  const prototype = Object.getPrototypeOf(value);
  if (
    !Array.isArray(value) &&
    prototype !== Object.prototype &&
    prototype !== null
  )
    return;
  seen.add(value);
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string')
      (value as Record<string, unknown>)[key] = redactText(entry);
    else scrub(entry, seen);
  }
}

// Browser noise with nothing to fix, and errors thrown by an extension's own
// code (CLEANLIST-PORTAL-78: "reading 'M_ID'", every frame chrome-extension://).
const IGNORED_ERROR =
  /^(?:Uncaught )?"?(?:Script error\.?|ResizeObserver loop completed with undelivered notifications\.)"?$/;
const EXTENSION_URL = /(?:chrome|moz|safari(?:-web)?)-extension:\/\//i;

/** The stack's top frame: the first line after the message. */
const topFrame = (stack: string | undefined) => stack?.split('\n')[1] ?? '';

export const rumBeforeSend: RumBeforeSend = (event) => {
  if (event.type === 'error') {
    const { message, stack } = event.error;
    if (IGNORED_ERROR.test(message) || EXTENSION_URL.test(topFrame(stack)))
      return false;
    // A stale chunk whose reload is already on its way: the reload fixes it.
    if (reloadPending() && isStaleChunkError(message)) return false;
  }
  scrub(event);
  return true;
};

export const logsBeforeSend = (event: object) => {
  scrub(event);
  return true;
};

export const telemetryEnv = (hostname = window.location.hostname) => {
  if (hostname.includes('staging')) return 'staging';
  // Deploy previews, Netlify permalinks and local dev run real builds, but
  // their errors are not production's and must not page the prod monitors.
  if (
    hostname.startsWith('preview.') ||
    hostname.endsWith('.netlify.app') ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  )
    return 'preview';
  return 'production';
};

// Crawlers and automated browsers aren't users: Googlebot replays old builds
// whose chunks are gone, and GoogleOther or headless scanners file
// "Failed to fetch" and "Blob is not a constructor" from their own runtime.
const BOT_UA =
  /bot\b|crawler|spider|GoogleOther|Google-InspectionTool|HeadlessChrome|Lighthouse/i;
export const isAutomatedBrowser = (
  userAgent = navigator.userAgent,
  webdriver = navigator.webdriver
) => webdriver === true || BOT_UA.test(userAgent);

/** Starts Datadog RUM + Logs. A no-op until the Netlify env vars are set. */
export function initDatadog() {
  const applicationId = import.meta.env.VITE_DD_RUM_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DD_CLIENT_TOKEN;
  if (!applicationId || !clientToken) return;
  if (isAutomatedBrowser()) return;
  const shared = {
    clientToken,
    site: (import.meta.env.VITE_DD_SITE ||
      'datadoghq.com') as RumInitConfiguration['site'],
    service: 'cleanlist-portal',
    env: telemetryEnv(),
    // Same value the source maps are uploaded under (netlify.toml).
    version: BUILD_ID,
  };
  try {
    datadogRum.init({
      ...shared,
      applicationId,
      // Every session, so no error is sampled away. Replay is not in the
      // slim bundle at all; PostHog and Clarity already record sessions.
      sessionSampleRate: 100,
      traceSampleRate: 50,
      allowedTracingUrls: [
        {
          match: /^https:\/\/(?:staging\.)?api\.cleanlist\.ai/,
          propagatorTypes: ['tracecontext', 'datadog'],
        },
      ],
      trackResources: true,
      trackLongTasks: true,
      // Click names come from on-screen text, which shows lead PII.
      trackUserInteractions: false,
      defaultPrivacyLevel: 'mask',
      beforeSend: rumBeforeSend,
    });
    datadogLogs.init({
      ...shared,
      // RUM already records errors; forwarding them again doubles the bill.
      forwardErrorsToLogs: false,
      forwardConsoleLogs: ['log', 'warn', 'error'],
      beforeSend: logsBeforeSend,
    });
  } catch {
    // Telemetry startup must never prevent the application from mounting.
  }
}

type ReportContext = {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};

/** Report a caught error. */
export function reportError(error: unknown, context: ReportContext = {}) {
  datadogRum.addError(error, { ...context.tags, ...context.extra });
}

/** Report something worth knowing that is not an error. */
export function reportInfo(message: string, extra?: Record<string, unknown>) {
  datadogLogs.logger.info(message, extra);
}
